import { useGetTranslations } from "apollo/hooks/queries";
import { Result } from "apollo/utils";
import { useSenderAccountContext } from "context/SenderAccountContext";
import {
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  REPOSITORY_EMAIL,
} from "core/consts";
import { isTranslationsAdmin } from "core/model/accounts";
import { QueryProgress } from "core/types";
import {
  SpinnerPage,
  SpinnerPageContainer,
} from "ds_legacy/components/Spinner";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { Caption, Title } from "ds_legacy/materials/typography";
import { RenderDelay } from "dsl/atoms/LoadingDelay";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useAcpNavigationHandlers } from "dsl/hooks";
import { useCallback, useEffect, useState } from "react";
import useGitHandler from "./gitHandler";
import {
  Locales,
  RepositoryType,
  TranslationsAndRepo,
  TranslationTypes,
} from "./types";
import { TranslationsPanel } from "./view";

export const locales: Locales = {
  de: "German",
  en: "English",
};

export const PANEL_DEFAULT = "panel";
export const EXPORT_PANEL = "export";

export function ExportPanel({
  emailTranslations,
  queryProgress,
  repo,
  setIndicator,
  setPanel,
  translations,
}: {
  queryProgress: QueryProgress;
  setIndicator: (indicator: boolean) => void;
  setPanel: (panel: string) => void;
} & TranslationsAndRepo) {
  const [commitLoading, setCommitLoading] = useState(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const commitChanges = useGitHandler();
  const toast = useToast();
  const [status, setStatus] = useState(
    "Fetching latest translations from the backend",
  );

  const translationArray = translations || emailTranslations;

  const exportTranslations = useCallback(async () => {
    setCommitLoading(QUERY_PROGRESS_PENDING);
    try {
      await commitChanges(
        repo === REPOSITORY_EMAIL
          ? {
              emailTranslations,
              repo,
              setStatus,
            }
          : {
              repo,
              setStatus,
              translations,
            },
      );
      setIndicator(true);
      toast({
        color: "success",
        message: "Translations updated successfully !",
      });

      setCommitLoading(QUERY_PROGRESS_SUCCEED);
    } catch (e) {
      console.error("[Translations] Export fail:", e);
      setIndicator(false);
      toast({
        color: "danger",
        message:
          "Did not manage to update translations, please contact a Recare dev",
      });
      setCommitLoading(QUERY_PROGRESS_FAILED);
    }
    setPanel(PANEL_DEFAULT);
  }, [translationArray, setPanel]);

  useEffect(() => {
    if (
      commitLoading === QUERY_PROGRESS_NOT_STARTED &&
      queryProgress === QUERY_PROGRESS_SUCCEED &&
      translationArray != null
    ) {
      if (translationArray?.length) exportTranslations();
      else {
        setIndicator(false);
        setPanel(PANEL_DEFAULT);
        toast({ color: "danger", message: "No translations to export" });
      }
    }
  }, [translationArray, queryProgress, exportTranslations, commitLoading]);

  return <LoadingView loadingState={status} />;
}

function ConnectedExportPanel({
  repo,
  setIndicator,
  setPanel,
}: {
  repo: RepositoryType;
  setIndicator: (indicator: boolean) => void;
  setPanel: (panel: string) => void;
}) {
  const [queryProgress, translations] = useGetTranslations(repo, {
    fetchPolicy: "network-only",
  });

  return (
    <ExportPanel
      queryProgress={queryProgress}
      repo={repo}
      setIndicator={setIndicator}
      setPanel={setPanel}
      translations={translations ?? []}
    />
  );
}

function LoadingView({
  loadingState,
}: {
  loadingState: string | null | undefined;
}) {
  const info = "The entire process can take up to 2 minutes";

  return (
    <RenderDelay>
      <SpinnerPageContainer>
        <VerticalLayout aligned>
          <SpinnerPage id="translations-export" />
          {loadingState && <Title>{loadingState}...</Title>}
          <Caption>{info}</Caption>
        </VerticalLayout>
      </SpinnerPageContainer>
    </RenderDelay>
  );
}

export function Panels({
  onExport,
  panel,
  repo,
  setPanel,
  translations,
}: {
  onExport: () => void;
  panel: string;
  repo: RepositoryType;
  setPanel: (p: string) => void;
  translations: Readonly<TranslationTypes> | undefined;
}) {
  const [indicator, setIndicator] = useState<boolean | null>(null);

  switch (panel) {
    case EXPORT_PANEL:
      return (
        <ConnectedExportPanel
          repo={repo}
          setIndicator={setIndicator}
          setPanel={setPanel}
        />
      );
    case PANEL_DEFAULT:
    default:
      return (
        <TranslationsPanel
          indicator={indicator}
          repo={repo}
          translationsArray={translations}
          onExport={onExport}
        />
      );
  }
}

function ConnectedTranslationsPanels({ repo }: { repo: RepositoryType }) {
  const [panel, setPanel] = useState(PANEL_DEFAULT);
  const [queryProgress, translationsArray] = useGetTranslations(repo);

  return (
    <Result
      data={translationsArray}
      getErrorLabel={() => "Could not get translations"}
      id="acp-translations-get"
      queryProgress={queryProgress}
    >
      {(translationsArray) => (
        <Panels
          panel={panel}
          repo={repo}
          setPanel={setPanel}
          translations={translationsArray}
          onExport={() => setPanel(EXPORT_PANEL)}
        />
      )}
    </Result>
  );
}

export default function AcpTranslations({ repo }: { repo: RepositoryType }) {
  const navigationHandlers = useAcpNavigationHandlers();
  const { account } = useSenderAccountContext();
  const toast = useToast();

  useEffect(() => {
    if (account != null && !isTranslationsAdmin(account)) {
      toast({
        color: "danger",
        message: "[ACP Translations] You are not allowed here.",
      });
      navigationHandlers.goToDashboard();
    }
  }, [account]);

  if (!account) return null;

  return <ConnectedTranslationsPanels repo={repo} />;
}
